import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Space,
  message,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ShoppingCartOutlined,
  FileTextOutlined,
  EyeOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const PurchaseOrders = () => {
  const queryClient = useQueryClient();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [stats, setStats] = useState({ total: 0, totalAmount: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [editingOrder, setEditingOrder] = useState(null);

  const isEditing = !!editingOrder;

  const fetchOrders = async (page = 1, search = searchTerm) => {
    setLoading(true);
    try {
      const params =
        `page=${page}&per_page=${pagination.pageSize}` +
        (search ? `&search=${encodeURIComponent(search)}` : '');
      const res = await api.get(`/admin/purchase-orders?${params}`);
      setOrders(res.data.data.data);
      setPagination((prev) => ({
        ...prev,
        current: res.data.data.current_page,
        total: res.data.data.total,
      }));
    } catch (err) {
      message.error('Failed to fetch purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await api.get('/admin/inventory/products?per_page=1000');
      setProducts(res.data.data.data || []);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/purchase-orders?per_page=1');
      const data = res.data.data;
      setStats({ total: data.total || 0, totalAmount: 0 });
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    fetchOrders(1, searchTerm);
    fetchProducts();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleSubmit = async (values) => {
    if (isEditing) {
      await handleUpdate(values);
    } else {
      try {
        const payload = {
          supplier_name: values.supplier_name,
          supplier_phone: values.supplier_phone || null,
          purchase_date: values.purchase_date.format('YYYY-MM-DD'),
          notes: values.notes || null,
          items: values.items.map((item) => ({
            product_id: item.product_id,
            quantity: item.quantity,
            unit_cost: item.unit_cost,
          })),
        };

        await api.post('/admin/purchase-orders', payload);
        message.success('Purchase order created and stock updated');
        setModalVisible(false);
        form.resetFields();
        fetchOrders();
        fetchStats();
        queryClient.invalidateQueries('products');
      } catch (err) {
        message.error(err.response?.data?.message || 'Failed to create purchase order');
      }
    }
  };

  const handleEdit = async (record) => {
    try {
      const res = await api.get(`/admin/purchase-orders/${record.id}`);
      const order = res.data.data;
      setEditingOrder(order);
      form.setFieldsValue({
        supplier_name: order.supplier_name,
        supplier_phone: order.supplier_phone || '',
        purchase_date: dayjs(order.purchase_date),
        notes: order.notes || '',
        items: order.items.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_cost: item.unit_cost,
        })),
      });
      setModalVisible(true);
    } catch (err) {
      message.error('Failed to load purchase order for editing');
    }
  };

  const handleUpdate = async (values) => {
    if (!editingOrder) return;
    try {
      const payload = {
        supplier_name: values.supplier_name,
        supplier_phone: values.supplier_phone || null,
        purchase_date: values.purchase_date.format('YYYY-MM-DD'),
        notes: values.notes || null,
        items: values.items.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_cost: item.unit_cost,
        })),
      };

      await api.put(`/admin/purchase-orders/${editingOrder.id}`, payload);
      message.success('Purchase order updated and stock adjusted');
      setModalVisible(false);
      setEditingOrder(null);
      form.resetFields();
      fetchOrders();
      fetchStats();
      queryClient.invalidateQueries('products');
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to update purchase order');
    }
  };

  const handleView = async (record) => {
    setViewLoading(true);
    setSelectedOrder(null);
    setViewModalVisible(true);
    try {
      const res = await api.get(`/admin/purchase-orders/${record.id}`);
      setSelectedOrder(res.data.data);
    } catch (err) {
      message.error('Failed to load purchase order details');
      setViewModalVisible(false);
    } finally {
      setViewLoading(false);
    }
  };

  const columns = [
    {
      title: 'Bill #',
      dataIndex: 'bill_number',
      key: 'bill_number',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
    },
    {
      title: 'Date',
      dataIndex: 'purchase_date',
      key: 'purchase_date',
      render: (date) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Items',
      key: 'item_count',
      dataIndex: 'item_count',
      render: (count) => <Tag>{count} items</Tag>,
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (val) => `Rs. ${parseFloat(val || 0).toLocaleString()}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = { completed: 'green', pending: 'orange', cancelled: 'red' };
        return <Tag color={colors[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleView(record)}>
            View
          </Button>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={6}>
            <Statistic
              title="Total Purchase Orders"
              value={stats.total}
              prefix={<FileTextOutlined />}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Statistic title="Products" value={products.length} prefix={<ShoppingCartOutlined />} />
          </Col>
          <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
            <Space>
              <Input.Search
                placeholder="Search by bill # or supplier..."
                allowClear
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onSearch={(value) => setSearchTerm(value)}
                style={{ width: 260 }}
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchOrders(pagination.current, searchTerm)}
              >
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingOrder(null);
                  form.resetFields();
                  form.setFieldsValue({ items: [{}], purchase_date: dayjs() });
                  setModalVisible(true);
                }}
              >
                New Purchase Order
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `${total} orders`,
          }}
          onChange={(pag) => fetchOrders(pag.current, searchTerm)}
          scroll={{ x: 900 }}
        />
      </Card>

      <Modal
        title={
          isEditing ? `Edit Purchase Order: ${editingOrder.bill_number}` : 'New Purchase Order'
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingOrder(null);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="supplier_name"
                label="Supplier Name"
                rules={[{ required: true, message: 'Enter supplier name' }]}
              >
                <Input placeholder="Supplier name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="supplier_phone"
                label="Supplier Phone"
                rules={[{ pattern: /^\+?\d+$/, message: 'Please enter numbers and + only' }]}
              >
                <Input
                  type="tel"
                  onKeyPress={(e) => e.key !== '+' && !/[0-9]/.test(e.key) && e.preventDefault()}
                  placeholder="Phone number"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="purchase_date"
                label="Purchase Date"
                rules={[{ required: true, message: 'Select date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="notes" label="Notes">
                <TextArea rows={1} placeholder="Optional notes" />
              </Form.Item>
            </Col>
          </Row>

          <Form.List
            name="items"
            rules={[
              {
                validator: async (_, items) => {
                  if (!items || items.length < 1 || items.every((i) => !i.product_id)) {
                    throw new Error('Add at least one item');
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }) => (
              <>
                <Title level={5}>Items</Title>
                {fields.length === 0 && <Empty description="No items added" />}
                {fields.map(({ key, name, ...rest }) => (
                  <Row key={key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        {...rest}
                        name={[name, 'product_id']}
                        rules={[{ required: true, message: 'Select product' }]}
                      >
                        <Select
                          showSearch
                          placeholder="Select product"
                          filterOption={(input, option) =>
                            (option?.label || '').toLowerCase().includes(input.toLowerCase())
                          }
                          options={products
                            .filter((p) => p.type === 'product')
                            .map((p) => ({
                              label: `${p.name} (Stock: ${p.stock_quantity ?? 0})`,
                              value: p.id,
                            }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={5}>
                      <Form.Item
                        {...rest}
                        name={[name, 'quantity']}
                        rules={[{ required: true, message: 'Qty' }]}
                      >
                        <InputNumber min={1} placeholder="Qty" style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={5}>
                      <Form.Item
                        {...rest}
                        name={[name, 'unit_cost']}
                        rules={[{ required: true, message: 'Cost' }]}
                      >
                        <InputNumber
                          min={0}
                          step={0.01}
                          placeholder="Unit cost"
                          style={{ width: '100%' }}
                          prefix="Rs."
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={6}>
                      {fields.length > 1 && (
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                          size="small"
                        />
                      )}
                    </Col>
                  </Row>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add({})}
                  block
                  icon={<PlusOutlined />}
                  style={{ marginBottom: 16 }}
                >
                  Add Item
                </Button>
              </>
            )}
          </Form.List>

          <Button type="primary" htmlType="submit" block size="large">
            {isEditing
              ? 'Update Purchase Order & Adjust Stock'
              : 'Create Purchase Order & Update Stock'}
          </Button>
        </Form>
      </Modal>

      <Modal
        title={`Purchase Order: ${selectedOrder?.bill_number || ''}`}
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setSelectedOrder(null);
        }}
        footer={
          <Button
            onClick={() => {
              setViewModalVisible(false);
              setSelectedOrder(null);
            }}
          >
            Close
          </Button>
        }
        width={800}
        loading={viewLoading}
      >
        {viewLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
        ) : selectedOrder ? (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <strong>Supplier:</strong> {selectedOrder.supplier_name}
              </Col>
              <Col span={8}>
                <strong>Date:</strong> {dayjs(selectedOrder.purchase_date).format('DD/MM/YYYY')}
              </Col>
              <Col span={8}>
                <strong>Status:</strong>{' '}
                <Tag color={selectedOrder.status === 'completed' ? 'green' : 'orange'}>
                  {selectedOrder.status}
                </Tag>
              </Col>
            </Row>
            {selectedOrder.notes && (
              <div style={{ marginBottom: 16 }}>
                <strong>Notes:</strong> {selectedOrder.notes}
              </div>
            )}
            <Table
              dataSource={selectedOrder.items || []}
              rowKey="id"
              pagination={false}
              columns={[
                { title: '#', key: 'idx', render: (_, __, idx) => idx + 1, width: 50 },
                {
                  title: 'Product',
                  dataIndex: 'product',
                  render: (p) => p?.name || 'N/A',
                },
                { title: 'Quantity', dataIndex: 'quantity', width: 100 },
                {
                  title: 'Unit Cost',
                  dataIndex: 'unit_cost',
                  width: 120,
                  render: (val) => `Rs. ${parseFloat(val || 0).toLocaleString()}`,
                },
                {
                  title: 'Subtotal',
                  dataIndex: 'subtotal',
                  width: 120,
                  render: (val) => `Rs. ${parseFloat(val || 0).toLocaleString()}`,
                },
              ]}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4} align="right">
                    <strong>Total:</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <strong>
                      Rs. {parseFloat(selectedOrder.total_amount || 0).toLocaleString()}
                    </strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
            {selectedOrder.histories && selectedOrder.histories.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <Title level={5}>
                  <HistoryOutlined /> Edit History
                </Title>
                {selectedOrder.histories.map((h) => (
                  <Card key={h.id} size="small" style={{ marginBottom: 8, background: '#fafafa' }}>
                    <Space direction="vertical" style={{ width: '100%' }} size={2}>
                      <div>
                        <Tag color={h.action === 'created' ? 'green' : 'blue'}>{h.action}</Tag>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          by {h.user?.full_name || h.user?.name || 'System'} on{' '}
                          {dayjs(h.created_at).format('DD/MM/YYYY HH:mm')}
                        </Text>
                      </div>
                      {h.changes && h.action === 'updated' && (
                        <div style={{ fontSize: 13, paddingLeft: 8 }}>
                          {Array.isArray(h.changes) ? (
                            h.changes.map((c, i) => (
                              <div key={i} style={{ color: '#555', marginTop: 2 }}>
                                {c.field === 'added' && (
                                  <span>
                                    ➕ Added <strong>{c.product_name}</strong> × {c.new_value}
                                  </span>
                                )}
                                {c.field === 'removed' && (
                                  <span>
                                    ➖ Removed <strong>{c.product_name}</strong> (was {c.old_value})
                                  </span>
                                )}
                                {c.field === 'quantity' && (
                                  <span>
                                    ✏️ <strong>{c.product_name}</strong> qty: {c.old_value} →{' '}
                                    {c.new_value}
                                  </span>
                                )}
                                {c.field === 'unit_cost' && (
                                  <span>
                                    ✏️ <strong>{c.product_name}</strong> cost: Rs.{c.old_value} →
                                    Rs.{c.new_value}
                                  </span>
                                )}
                              </div>
                            ))
                          ) : (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {typeof h.changes === 'string'
                                ? h.changes
                                : JSON.stringify(h.changes)}
                            </Text>
                          )}
                        </div>
                      )}
                    </Space>
                  </Card>
                ))}
              </div>
            )}
          </>
        ) : null}
      </Modal>
    </div>
  );
};

export default PurchaseOrders;
